import { z } from "zod";
import { RichText } from "../RichText";
import { ctaColorEnum, ctaClasses } from "../ctaColors";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";

/* Per-layout container width map for band/boxed layouts */
const BAND_WIDTH = {
  contained: "max-w-4xl mx-auto px-6",
  wide:      "max-w-5xl mx-auto px-6",
  full:      "max-w-6xl mx-auto px-6",
} as const;

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Ready to Get Started?"),
  body: z.string().default("Book a session with an experienced professional today."),
  ctaLabel: z.string().default("Book Now"),
  ctaHref: z.string().default("/book"),
  layout: z.enum(["band", "split", "boxed"]).default("band"),
  imageUrl: z.string().optional(),
  ctaTarget: z.enum(["_self", "_blank"]).default("_self"),
  ctaColor: ctaColorEnum.optional(),
  microcopy: z.string().optional(),
  backgroundStyle: backgroundStyleEnum.default("primary"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* ---------- Layout: Band (default, existing behavior) ---------- */

function BandLayout({ safe }: { safe: Props }) {
  const container = BAND_WIDTH[safe.width];
  return (
    <SectionShell globals={safe} bare>
      <div className={`${container} text-center`}>
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold mb-4"
          data-prop="heading"
        />
        <RichText
          as="p"
          html={safe.body}
          className="text-lg @md:text-xl opacity-90 mb-8"
          data-prop="body"
        />
        {safe.ctaLabel && safe.ctaHref && (
          <a
            href={safe.ctaHref}
            target={safe.ctaTarget}
            rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
            className={`inline-block font-semibold px-8 py-3 rounded-lg transition-opacity ${ctaClasses(safe.ctaColor || "light")}`}
          >
            <RichText html={safe.ctaLabel} data-prop="ctaLabel" />
          </a>
        )}
        {safe.microcopy && (
          <p className="text-sm opacity-80 mt-2">{safe.microcopy}</p>
        )}
      </div>
    </SectionShell>
  );
}

/* ---------- Layout: Split (text left, CTA right) ---------- */

function SplitLayout({ safe }: { safe: Props }) {
  return (
    <SectionShell globals={safe}>
        <div className="grid grid-cols-1 @md:grid-cols-2 gap-8 @md:gap-16 items-center">
          <div>
            <RichText
              as={safe.headingLevel}
              html={safe.heading}
              className="text-3xl @md:text-[2.5rem] font-bold mb-4"
              data-prop="heading"
            />
            <RichText
              as="p"
              html={safe.body}
              className="text-lg opacity-90"
              data-prop="body"
            />
          </div>
          <div className="flex @md:justify-end items-center">
            <div>
              {safe.ctaLabel && safe.ctaHref && (
                <a
                  href={safe.ctaHref}
                  target={safe.ctaTarget}
                  rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
                  className={`inline-block font-semibold px-10 py-4 rounded-lg text-lg transition-opacity ${ctaClasses(safe.ctaColor || "light")}`}
                >
                  <RichText html={safe.ctaLabel} data-prop="ctaLabel" />
                </a>
              )}
              {safe.microcopy && (
                <p className="text-sm opacity-80 mt-2 text-center">{safe.microcopy}</p>
              )}
            </div>
          </div>
        </div>
    </SectionShell>
  );
}

/* ---------- Layout: Boxed (floating card) ---------- */

function BoxedLayout({ safe }: { safe: Props }) {
  const container = BAND_WIDTH[safe.width];
  return (
    <SectionShell globals={safe} bare>
      <div className={container}>
        <div className="bg-white text-text rounded-2xl shadow-lg p-10 @md:p-14 text-center">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          <RichText
            as="p"
            html={safe.body}
            className="text-lg opacity-80 mb-8 max-w-2xl mx-auto"
            data-prop="body"
          />
          {safe.ctaLabel && safe.ctaHref && (
            <a
              href={safe.ctaHref}
              target={safe.ctaTarget}
              rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
              className={`inline-block font-semibold px-8 py-3 rounded-lg transition-opacity ${ctaClasses(safe.ctaColor || "primary")}`}
            >
              <RichText html={safe.ctaLabel} data-prop="ctaLabel" />
            </a>
          )}
          {safe.microcopy && (
            <p className="text-sm opacity-80 mt-2">{safe.microcopy}</p>
          )}
        </div>
      </div>
    </SectionShell>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  switch (safe.layout) {
    case "split":
      return <SplitLayout safe={safe} />;
    case "boxed":
      return <BoxedLayout safe={safe} />;
    case "band":
    default:
      return <BandLayout safe={safe} />;
  }
}
